import { FC, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import {
  fetchNotificationPreferences,
  saveNotificationPreferences,
  type NotificationChannelDefinition,
  type NotificationEventChannel,
  type NotificationChannelState,
  type NotificationEventFamily,
  type NotificationPreferenceResponse,
} from './notification-preferences-api';

type NotificationDraft = Record<string, boolean>;

const buildEditableDraft = (eventFamilies: NotificationEventFamily[]): NotificationDraft => (
  Object.fromEntries(
    eventFamilies
      .filter((eventFamily) => eventFamily.channels.in_app.editable)
      .map((eventFamily) => [eventFamily.key, eventFamily.channels.in_app.enabled])
  )
);

const hasEditableDraftChanges = (
  response: NotificationPreferenceResponse | null,
  draft: NotificationDraft
) => (
  response?.event_families.some((eventFamily) => (
    eventFamily.channels.in_app.editable
    && (draft[eventFamily.key] ?? eventFamily.channels.in_app.enabled) !== eventFamily.channels.in_app.enabled
  )) ?? false
);

const channelStateBadgeClass = (state: NotificationChannelState) => {
  switch (state) {
    case 'required':
      return 'bg-primary-transparent text-primary';
    case 'enabled':
      return 'bg-success-transparent text-success';
    case 'disabled_by_user':
      return 'bg-light text-default';
    case 'degraded':
      return 'bg-warning-transparent text-warning';
    case 'unavailable_policy':
      return 'bg-secondary-transparent text-secondary';
    case 'unavailable_platform':
    default:
      return 'bg-light text-muted';
  }
};

const channelStateLabel = (state: NotificationChannelState) => {
  switch (state) {
    case 'required':
      return 'Required';
    case 'enabled':
      return 'On';
    case 'disabled_by_user':
      return 'Off';
    case 'degraded':
      return 'Limited';
    case 'unavailable_policy':
      return 'Policy managed';
    case 'unavailable_platform':
    default:
      return 'Unavailable here';
  }
};

const overviewBadgeClass = (channel: NotificationChannelDefinition) => (
  channel.supported
    ? 'bg-success-transparent text-success'
    : 'bg-light text-muted border'
);

const overviewBadgeLabel = (channel: NotificationChannelDefinition) => {
  if (channel.supported) {
    return channel.status === 'degraded' ? 'Limited' : 'Active';
  }

  return channel.status === 'unavailable_policy' ? 'Policy managed' : 'Unavailable here';
};

const channelToggleLabel = (channel: NotificationEventChannel) => {
  switch (channel.state) {
    case 'required':
      return 'Required for this alert';
    case 'unavailable_policy':
      return 'Managed by your organization';
    case 'unavailable_platform':
      return 'Unavailable in this environment';
    case 'degraded':
      return 'Temporarily limited';
    default:
      return 'Send to this channel';
  }
};

const eventHelperCopy = (eventFamily: NotificationEventFamily) => {
  if (eventFamily.channels.in_app.state === 'required') {
    return 'This alert stays on so you do not miss material schedule or security changes.';
  }

  if (eventFamily.channels.in_app.state === 'unavailable_policy') {
    return 'Your organization controls this delivery path, so you cannot change it here.';
  }

  if (eventFamily.channels.in_app.state === 'unavailable_platform') {
    return 'This notification type is unavailable in the current CARES environment.';
  }

  return 'Reading or deleting an inbox item later will not change this future delivery setting.';
};

const NotificationPreferencesPanel: FC = () => {
  const [response, setResponse] = useState<NotificationPreferenceResponse | null>(null);
  const [draft, setDraft] = useState<NotificationDraft>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const loadPreferences = async () => {
      try {
        const nextResponse = await fetchNotificationPreferences();

        if (!active) {
          return;
        }

        setResponse(nextResponse);
        setDraft(buildEditableDraft(nextResponse.event_families));
        setError(null);
      } catch (requestError) {
        if (!active) {
          return;
        }

        setError(requestError instanceof Error ? requestError.message : 'Unable to load notification settings.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void loadPreferences();

    return () => {
      active = false;
    };
  }, []);

  const updateDraft = (eventFamilyKey: string, enabled: boolean) => {
    setDraft((current) => ({
      ...current,
      [eventFamilyKey]: enabled,
    }));
  };

  const saveDisabled = saving || !hasEditableDraftChanges(response, draft);

  const handleSave = async () => {
    if (!response) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const nextResponse = await saveNotificationPreferences({
        event_families: response.event_families
          .filter((eventFamily) => eventFamily.channels.in_app.editable)
          .map((eventFamily) => ({
            key: eventFamily.key,
            channels: {
              in_app: draft[eventFamily.key] ?? eventFamily.channels.in_app.enabled,
            },
          })),
      });

      setResponse(nextResponse);
      setDraft(buildEditableDraft(nextResponse.event_families));
      setSuccess('Notification preferences saved.');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to save notification settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="border custom-card mb-0">
        <Card.Body className="d-flex align-items-center gap-2 text-muted">
          <Spinner size="sm" animation="border" />
          Loading notification preferences...
        </Card.Body>
      </Card>
    );
  }

  if (!response) {
    return (
      <Alert variant="danger" className="mb-0">
        {error ?? 'Notification settings could not be loaded.'}
      </Alert>
    );
  }

  return (
    <div className="d-flex flex-column gap-4">
      {error ? <Alert variant="danger" className="mb-0">{error}</Alert> : null}
      {success ? <Alert variant="success" className="mb-0">{success}</Alert> : null}

      <Card className="border custom-card mb-0">
        <Card.Body>
          <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 mb-4">
            <div>
              <h5 className="fw-medium mb-2">Delivery overview</h5>
              <p className="text-muted mb-0">
                Choose how CARES alerts you about schedule and request activity. Reading or deleting items in your inbox does not change future delivery settings.
              </p>
            </div>
            <div className="text-muted fs-13">
              Mandatory schedule and security notices fall back to <span className="fw-medium text-default">{response.delivery_status.fallback_channel_for_mandatory_events.replace('_', ' ')}</span>.
            </div>
          </div>

          <Row className="g-3">
            {Object.entries(response.channels).map(([key, channel]) => (
              <Col key={key} md={6} xl={3}>
                <Card className="custom-card border mb-0 h-100">
                  <Card.Body>
                    <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                      <div>
                        <p className="fw-medium mb-1">{channel.label}</p>
                        <p className="fs-12 text-muted mb-0">{channel.description}</p>
                      </div>
                      <Badge bg="" className={overviewBadgeClass(channel)}>
                        {overviewBadgeLabel(channel)}
                      </Badge>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>

      <Card className="border custom-card mb-0">
        <Card.Body>
          <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 mb-4">
            <div>
              <h5 className="fw-medium mb-2">Event preferences</h5>
              <p className="text-muted mb-0">
                Some critical updates always stay on so you do not miss material schedule or security changes.
              </p>
            </div>
            <Button variant="primary" onClick={() => void handleSave()} disabled={saveDisabled}>
              {saving ? 'Saving...' : 'Save preferences'}
            </Button>
          </div>

          <div className="d-flex flex-column gap-3">
            {response.event_families.map((eventFamily) => {
              const inAppEnabled = draft[eventFamily.key] ?? eventFamily.channels.in_app.enabled;

              return (
                <Card key={eventFamily.key} className="custom-card border mb-0">
                  <Card.Body>
                    <Row className="g-3 align-items-center">
                      <Col xl={5}>
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                          <h6 className="fw-medium mb-0">{eventFamily.label}</h6>
                          {eventFamily.mandatory ? (
                            <Badge bg="" className="bg-primary-transparent text-primary">
                              Required
                            </Badge>
                          ) : null}
                        </div>
                        <p className="text-muted mb-2">{eventFamily.description}</p>
                        <p className="fs-12 text-muted mb-0">{eventHelperCopy(eventFamily)}</p>
                      </Col>

                      <Col xl={7}>
                        <Row className="g-3">
                          {(['in_app', 'email', 'sms', 'push'] as const).map((channelKey) => {
                            const channel = eventFamily.channels[channelKey];
                            const checked = channelKey === 'in_app' ? inAppEnabled : channel.enabled;

                            return (
                              <Col key={channelKey} sm={6} xxl={3}>
                                <Card className="border mb-0 h-100">
                                  <Card.Body className="p-3">
                                    <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                                      <span className="fw-medium fs-13">{channel.label}</span>
                                      <Badge bg="" className={channelStateBadgeClass(channel.state)}>
                                        {channelStateLabel(channel.state)}
                                      </Badge>
                                    </div>
                                    <Form.Check
                                      type="switch"
                                      id={`${eventFamily.key}-${channelKey}`}
                                      checked={checked}
                                      disabled={!channel.editable || saving}
                                      onChange={(event) => updateDraft(eventFamily.key, event.target.checked)}
                                      label={channelToggleLabel(channel)}
                                    />
                                  </Card.Body>
                                </Card>
                              </Col>
                            );
                          })}
                        </Row>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        </Card.Body>
      </Card>

      <Row className="g-3">
        <Col xl={6}>
          <Card className="border custom-card mb-0 h-100">
            <Card.Body>
              <h5 className="fw-medium mb-2">Reminder and digest</h5>
              <p className="text-muted mb-3">
                Reminder and digest controls are unavailable in the current CARES environment.
              </p>
              <Form.Check
                type="switch"
                id="notification-digest-disabled"
                checked={response.global_settings.digest_mode !== 'off'}
                disabled
                label="Digest summaries unavailable in this environment"
              />
              <Form.Check
                type="switch"
                id="notification-reminders-disabled"
                checked={response.global_settings.schedule_reminder_enabled}
                disabled
                label="Schedule reminders unavailable in this environment"
                className="mt-3"
              />
            </Card.Body>
          </Card>
        </Col>
        <Col xl={6}>
          <Card className="border custom-card mb-0 h-100">
            <Card.Body>
              <h5 className="fw-medium mb-2">Quiet hours and fallback</h5>
              <p className="text-muted mb-3">
                Quiet-hour controls are unavailable in the current CARES environment.
              </p>
              <Form.Check
                type="switch"
                id="notification-quiet-hours-disabled"
                checked={response.global_settings.quiet_hours_enabled}
                disabled
                label="Quiet hours unavailable in this environment"
              />
              <Alert variant="light" className="mt-3 mb-0">
                <div className="fw-medium mb-1">Sender fallback rule</div>
                <div className="text-muted fs-13">{response.delivery_status.sender_fallback_rule}</div>
              </Alert>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default NotificationPreferencesPanel;
